// src/views/litellm/components/MonitorFilterBar.jsx
import React from "react";
import FilterDropdown from "../../../components/ui-components/FilterDropdown";

// Period presets. Stored as the `hours` filter (0 = today/unfiltered); FilterDropdown compares
// string values, so options are strings and converted back on change.
const PERIOD_OPTIONS = [
  { value: "1", label: "1h" },
  { value: "6", label: "6h" },
  { value: "0", label: "Today" },
  { value: "24", label: "24h" },
  { value: "168", label: "7d" },
];

// A thin vertical rule separating control clusters — the same divider the /cost-analyzer
// scope row uses between its controls.
const Divider = () => <span aria-hidden style={{ width: 1, height: 16 }} className="bg-card-bd" />;

// MonitorFilterBar is the page's analysis scope row, rendered into the global header's
// `row3-filters` slot. It uses the same control grammar as /cost-analyzer: prefix-labelled
// FilterDropdowns (all height 32) for the choices, and a plain checkbox for the boolean overlay —
// deliberately not segmented pills, so the monitor reads as the same product surface. Period /
// granularity / model / key drive the summary query via `onFilterChange`; metric / tz / cumulative
// are display-only view state owned by the page and passed straight through.
export default function MonitorFilterBar({
  filter = {},
  onFilterChange = () => {},
  modelOptions = [],
  keyOptions = [],
  metricKey,
  setMetricKey,
  tz,
  setTz,
  cumulative,
  setCumulative,
}) {
  const granularity = filter.granularity === "minute" ? "minute" : "hour";
  const period = filter.hours || 0;
  const minuteDisabled = period === 168; // a minute series over 7 days is too dense to be useful

  return (
    <div className="flex w-full flex-wrap items-center gap-x-3 gap-y-2.5">
      {/* Time: which window, how finely, in whose clock. */}
      <FilterDropdown
        ariaLabel="Period"
        prefix="Period:"
        value={String(period)}
        onChange={(v) => {
          const hours = Number(v);
          onFilterChange(
            hours === 168 && granularity === "minute"
              ? { hours: hours || undefined, granularity: "hour" }
              : { hours: hours || undefined }
          );
        }}
        options={PERIOD_OPTIONS}
        minWidth={130}
        height={32}
      />
      <FilterDropdown
        ariaLabel="Granularity"
        prefix="Granularity:"
        value={granularity}
        onChange={(g) => onFilterChange({ granularity: g })}
        options={[
          { value: "hour", label: "Hour" },
          { value: "minute", label: "Minute", disabled: minuteDisabled },
        ]}
        minWidth={150}
        height={32}
      />
      <FilterDropdown
        ariaLabel="Timezone"
        prefix="Timezone:"
        value={tz}
        onChange={setTz}
        options={[
          { value: "UTC", label: "UTC" },
          { value: "Local", label: "Local" },
        ]}
        minWidth={140}
        height={32}
      />

      <Divider />

      {/* Measure: which series the bars plot. */}
      <FilterDropdown
        ariaLabel="Metric"
        prefix="Metric:"
        value={metricKey}
        onChange={setMetricKey}
        options={[
          { value: "cost", label: "Cost" },
          { value: "tokens", label: "Tokens" },
          { value: "events", label: "Events" },
        ]}
        minWidth={130}
        height={32}
      />

      <Divider />

      {/* Segmentation: slice by model / key, overlay the cumulative burn line. */}
      <FilterDropdown
        ariaLabel="Model"
        prefix="Model:"
        value={filter.model || ""}
        onChange={(v) => onFilterChange({ model: v || undefined })}
        options={[{ value: "", label: "All models" }, ...modelOptions.map((mo) => ({ value: mo, label: mo }))]}
        minWidth={160}
        height={32}
      />
      <FilterDropdown
        ariaLabel="API key"
        prefix="Key:"
        value={filter.source_key || ""}
        onChange={(v) => onFilterChange({ source_key: v || undefined })}
        options={[{ value: "", label: "All keys" }, ...keyOptions]}
        minWidth={150}
        height={32}
      />
      <label className="flex items-center gap-1.5 text-[13px] text-text-2">
        <input
          type="checkbox"
          checked={cumulative}
          onChange={(e) => setCumulative(e.target.checked)}
        />
        Cumulative total
      </label>
    </div>
  );
}
